import React, { useState, useMemo } from "react";
import {
  Box,
  Modal,
  Grid,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { GridSearchIcon } from "@mui/x-data-grid";
import { useAuth } from "src/hooks/useAuth.hook";

const TeamsModal = ({ open, onClose, teams, onSelectTeam }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [selectedLetter, setSelectedLetter] = useState("");
  const brandGradient = theme.palette.gradients?.brand || "linear-gradient(135deg,#2196f3,#7c3aed)";
  const brandHoverGradient = theme.palette.gradients?.brandHover || "linear-gradient(135deg,#7c3aed,#2196f3)";
  const modalSurface = theme.palette.mode === "dark"
    ? alpha(theme.palette.background.paper, 0.95)
    : theme.palette.background.paper;
  const cardBase = theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.03)
    : alpha(theme.palette.primary.main, 0.04);
  const cardBorder = alpha(theme.palette.primary.main, 0.2);

  const filteredTeams = useMemo(() => {
    let updatedTeams = [...teams];

    if (teams.length === 1 && teams[0].teamType === "T") {
      const companyTeam = {
        teamId: user.company.id,
        companyLogo: user.company.companyLogo,
        teamType: user.company.companyType,
        teamName: user.company.name,
        isDisabled: true, // Mark the parent company as disabled
      };
      updatedTeams = [companyTeam, ...teams];
    }

    return updatedTeams.filter((team, index) => {
      const isSpecialTeam =
        index === 0 && (team.teamType === "P" || team.teamType === "GP");
      const matchesSearch = team.teamName
        .toLowerCase()
        .includes(search.toLowerCase());
      const matchesLetter =
        selectedLetter === "" ||
        (selectedLetter === "#" &&
          (/^[\d@#$%&*!]/.test(team.teamName))) ||
        team.teamName.toLowerCase().startsWith(selectedLetter.toLowerCase());
      return isSpecialTeam || (matchesSearch && matchesLetter);
    });
  }, [teams, search, selectedLetter, user.company]);

  const handleLetterClick = (letter) => {
    setSelectedLetter((prev) => (prev === letter ? "" : letter));
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: "70%", md: "50%" },
          maxHeight: { xs: "90%", sm: "80%" },
          bgcolor: modalSurface,
          boxShadow: theme.palette.mode === "dark"
            ? "0 45px 80px rgba(2,6,23,0.65)"
            : "0 40px 90px rgba(15,23,42,0.25)",
          borderRadius: "6px",
          p: 3,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              fontSize: "1.25rem",
              color: theme.palette.text.primary,
            }}
          >
            Select a Team
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              borderRadius: 1,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.18)
              }
            }}
          >
            <span style={{ fontSize: "1.5rem", fontWeight: "bold" }}>X</span>
          </IconButton>
        </Box>

        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Search team"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <GridSearchIcon
                  sx={{ color: theme.palette.mode === "dark" ? theme.palette.text.secondary : theme.palette.text.disabled }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 3,
            borderRadius: "10px",
            "& .MuiOutlinedInput-root": {
              backgroundColor: theme.palette.mode === "dark"
                ? alpha(theme.palette.common.white, 0.04)
                : alpha(theme.palette.primary.main, 0.04),
              borderRadius: "10px",
              transition: "box-shadow 0.2s ease, border-color 0.2s ease",
              "& fieldset": {
                borderColor: cardBorder,
              },
              "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
              },
              "&.Mui-focused": {
                boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />

        {/* Teams Grid */}
        <Box sx={{ flexGrow: 1, overflowY: "auto", mb: 3 }}>
          <Grid container spacing={2}>
            {filteredTeams.map((team) => (
              <Grid item xs={12} sm={6} key={team.teamId}>
                <Box
                  onClick={() => !team.isDisabled && onSelectTeam(team)}
                  sx={{
                    p: 2,
                    border: `1px solid ${cardBorder}`,
                    borderRadius: "6px",
                    textAlign: "center",
                    cursor: team.isDisabled ? "not-allowed" : "pointer",
                    backgroundColor: team.isDisabled
                      ? alpha(theme.palette.text.disabled, 0.15)
                      : cardBase,
                    transition: "all 0.3s ease",
                    "& .team-name": {
                      color: theme.palette.text.primary,
                      transition: "color 0.3s ease",
                    },
                    "&:hover": {
                      backgroundImage: team.isDisabled ? "none" : brandGradient,
                      boxShadow: team.isDisabled ? "none" : "0 12px 24px rgba(33,150,243,0.25)",
                      color: team.isDisabled ? "inherit" : theme.palette.primary.contrastText,
                      "& .team-name": {
                        color: team.isDisabled
                          ? theme.palette.text.primary
                          : theme.palette.primary.contrastText,
                      },
                    },
                  }}
                >
                  <Typography
                    className="team-name"
                    variant={
                      (team.teamType === "P" || team.teamType === "GP") &&
                      team.teamType === user.company.companyType
                        ? "h6"
                        : "body1"
                    }
                    sx={{
                      fontWeight:
                        (team.teamType === "P" || team.teamType === "GP") &&
                        team.teamType === user.company.companyType
                          ? "bold"
                          : "normal",                  
                    }}
                  >
                    {team.teamType === user.company.companyType && (
                      <span className="mr-1">⭐</span>
                    )}
                    <span className="mr-1">{team.teamName}</span>
                    {team.teamType === "P" &&
                      team.teamType === user.company.companyType && (
                        <span className="mr-1">(Parent Company)</span>
                      )}
                    {team.teamType === "GP" &&
                      team.teamType === user.company.companyType && (
                        <span className="mr-1">(Grand Parent)</span>
                      )}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            mt: 2,
            p: 1,
            borderTop: `1px solid ${cardBorder}`,
            bgcolor: theme.palette.mode === "dark"
              ? alpha(theme.palette.background.paper, 0.7)
              : theme.palette.background.paper,
          }}
        >
          {["#", ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("")].map((letter) => (
            <Button
              key={letter}
              onClick={() => handleLetterClick(letter)}
              sx={{
                minWidth: 32,
                m: 0.5,
                fontSize: "0.875rem",
                fontWeight: "bold",
                color: selectedLetter === letter
                  ? theme.palette.primary.contrastText
                  : theme.palette.text.primary,
                backgroundImage:
                  selectedLetter === letter ? brandGradient : "none",
                backgroundColor:
                  selectedLetter === letter
                    ? "transparent"
                    : theme.palette.mode === "dark"
                      ? alpha(theme.palette.common.white, 0.08)
                      : alpha(theme.palette.primary.main, 0.08),
                borderRadius: "999px",
                px: 1.5,
                boxShadow: selectedLetter === letter ? "0 8px 16px rgba(33,150,243,0.25)" : "none",
                "&:hover": {
                  backgroundImage: brandHoverGradient,
                  color: theme.palette.primary.contrastText,
                },
              }}
            >
              {letter}
            </Button>
          ))}
        </Box>
      </Box>
    </Modal>
  );
};

export default TeamsModal;
